import React, { useState, useEffect } from 'react';
import {
  Search,
  Building2,
  Plus,
  Edit2,
  Trash2,
  Users,
  Settings,
  Crown,
  CheckCircle2,
  X,
  AlertCircle,
  Palette,
  Globe,
  Shield
} from 'lucide-react';
import {
  fetchCompanies,
  createCompany,
  updateCompany,
  updateCompanyFeature,
  AVAILABLE_FEATURES,
  getFeaturesByTier,
  type Company
} from '../lib/supabase';

export function CompanyManagement() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    size: 'medium' as Company['size'],
    subscription_tier: 'professional' as Company['subscription_tier'],
    admin_email: '',
    admin_first_name: '',
    admin_last_name: '',
    branding: {
      primary_color: '#e62336',
      logo_url: '',
      custom_domain: ''
    },
    settings: {
      default_language: 'NL' as 'NL' | 'FR' | 'EN',
      timezone: 'Europe/Brussels',
      currency: 'EUR'
    }
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setIsLoading(true);
      const data = await fetchCompanies();
      setCompanies(data);
    } catch (err) {
      setError('Failed to load companies');
      console.error('Error loading companies:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Create company
      const newCompany = await createCompany({
        name: formData.name,
        industry: formData.industry,
        size: formData.size,
        subscription_tier: formData.subscription_tier,
        branding: formData.branding,
        settings: formData.settings,
        admin_user_id: '', // Will be set after creating admin user
        is_active: true
      });

      // Set up default features based on subscription tier
      const defaultFeatures = getFeaturesByTier(formData.subscription_tier);
      for (const featureKey of defaultFeatures) {
        await updateCompanyFeature(newCompany.id, featureKey, true);
      }

      await loadCompanies();
      setIsCreateModalOpen(false);
      resetForm();
    } catch (err) {
      setError('Failed to create company');
      console.error('Error creating company:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      industry: company.industry,
      size: company.size,
      subscription_tier: company.subscription_tier,
      admin_email: '',
      admin_first_name: '',
      admin_last_name: '',
      branding: company.branding,
      settings: company.settings
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany) return;

    setIsLoading(true);
    setError(null);

    try {
      await updateCompany(selectedCompany.id, {
        name: formData.name,
        industry: formData.industry,
        size: formData.size,
        subscription_tier: formData.subscription_tier,
        branding: formData.branding,
        settings: formData.settings
      });

      await loadCompanies();
      setIsEditModalOpen(false);
      setSelectedCompany(null);
      resetForm();
    } catch (err) {
      setError('Failed to update company');
      console.error('Error updating company:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      industry: '',
      size: 'medium',
      subscription_tier: 'professional',
      admin_email: '',
      admin_first_name: '',
      admin_last_name: '',
      branding: {
        primary_color: '#e62336',
        logo_url: '',
        custom_domain: ''
      },
      settings: {
        default_language: 'NL',
        timezone: 'Europe/Brussels',
        currency: 'EUR'
      }
    });
  };

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    company.industry.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getSubscriptionBadge = (tier: Company['subscription_tier']) => {
    switch (tier) {
      case 'basic':
        return { color: 'bg-gray-100 text-gray-800', icon: CheckCircle2 };
      case 'professional':
        return { color: 'bg-blue-100 text-blue-800', icon: Shield };
      case 'enterprise':
        return { color: 'bg-purple-100 text-purple-800', icon: Crown };
      default:
        return { color: 'bg-gray-100 text-gray-800', icon: AlertCircle };
    }
  };

  if (isLoading && companies.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-jaimy-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading companies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <div>
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Management</h1>
          <p className="text-gray-600 mt-1">Manage company accounts and subscriptions</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-64">
            <input
              type="text"
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="gradient-bg text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            <Plus className="h-5 w-5" />
            <span>Add Company</span>
          </button>
        </div>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCompanies.map((company) => {
          const subscriptionBadge = getSubscriptionBadge(company.subscription_tier);
          const enabledFeatures = company.features?.filter(f => f.is_enabled).length || 0;
          
          return (
            <div
              key={company.id}
              className="bg-white rounded-lg shadow-sm border border-gray-100 hover:border-jaimy-red transition-colors overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="h-12 w-12 rounded-lg flex items-center justify-center text-white"
                      style={{ backgroundColor: company.branding.primary_color }}
                    >
                      {company.branding.logo_url ? (
                        <img 
                          src={company.branding.logo_url} 
                          alt={company.name}
                          className="h-8 w-8 object-contain"
                        />
                      ) : (
                        <Building2 className="h-6 w-6" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{company.name}</h3>
                      <p className="text-sm text-gray-600 capitalize">{company.industry}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${subscriptionBadge.color}`}>
                    <subscriptionBadge.icon className="h-3 w-3 mr-1" />
                    {company.subscription_tier}
                  </span>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Company Size</span>
                    <span className="font-medium capitalize">{company.size}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Active Features</span>
                    <span className="font-medium">{enabledFeatures}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Language</span>
                    <span className="font-medium">{company.settings.default_language}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Status</span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      company.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {company.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="mt-6 flex justify-between items-center">
                  <div className="text-xs text-gray-500">
                    Created {new Date(company.created_at).toLocaleDateString()}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditCompany(company)}
                      className="text-gray-400 hover:text-jaimy-red transition-colors"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        // Handle company settings
                      }}
                      className="text-gray-400 hover:text-jaimy-red transition-colors"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Company Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Create New Company</h2>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateCompany} className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Industry *
                    </label>
                    <select
                      required
                      value={formData.industry}
                      onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    >
                      <option value="">Select Industry</option>
                      <option value="real_estate">Real Estate</option>
                      <option value="insurance">Insurance</option>
                      <option value="banking">Banking</option>
                      <option value="retail">Retail</option>
                      <option value="healthcare">Healthcare</option>
                      <option value="manufacturing">Manufacturing</option>
                      <option value="technology">Technology</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Size *
                    </label>
                    <select
                      required
                      value={formData.size}
                      onChange={(e) => setFormData(prev => ({ ...prev, size: e.target.value as Company['size'] }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    >
                      <option value="small">Small (1-50 employees)</option>
                      <option value="medium">Medium (51-250 employees)</option>
                      <option value="large">Large (251-1000 employees)</option>
                      <option value="enterprise">Enterprise (1000+ employees)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subscription Tier *
                    </label>
                    <select
                      required
                      value={formData.subscription_tier}
                      onChange={(e) => setFormData(prev => ({ ...prev, subscription_tier: e.target.value as Company['subscription_tier'] }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    >
                      <option value="basic">Basic</option>
                      <option value="professional">Professional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Branding */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Branding
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Color
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={formData.branding.primary_color}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          branding: { ...prev.branding, primary_color: e.target.value }
                        }))}
                        className="h-10 w-16 border border-gray-200 rounded-lg"
                      />
                      <input
                        type="text"
                        value={formData.branding.primary_color}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          branding: { ...prev.branding, primary_color: e.target.value }
                        }))}
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Logo URL
                    </label>
                    <input
                      type="url"
                      value={formData.branding.logo_url}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        branding: { ...prev.branding, logo_url: e.target.value }
                      }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </div>
              </div>

              {/* Settings */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Settings
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Language
                    </label>
                    <select
                      value={formData.settings.default_language}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        settings: { ...prev.settings, default_language: e.target.value as 'NL' | 'FR' | 'EN' }
                      }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    >
                      <option value="NL">Nederlands</option>
                      <option value="FR">Français</option>
                      <option value="EN">English</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timezone
                    </label>
                    <select
                      value={formData.settings.timezone}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        settings: { ...prev.settings, timezone: e.target.value }
                      }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    >
                      <option value="Europe/Brussels">Europe/Brussels</option>
                      <option value="Europe/Paris">Europe/Paris</option>
                      <option value="Europe/London">Europe/London</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Currency
                    </label>
                    <select
                      value={formData.settings.currency}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        settings: { ...prev.settings, currency: e.target.value }
                      }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    >
                      <option value="EUR">EUR (€)</option>
                      <option value="USD">USD ($)</option>
                      <option value="GBP">GBP (£)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Creating...' : 'Create Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
      {isEditModalOpen && selectedCompany && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Edit Company</h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdateCompany} className="p-6 space-y-6">
              {/* Same form fields as create modal */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Industry *
                    </label>
                    <select
                      required
                      value={formData.industry}
                      onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    >
                      <option value="">Select Industry</option>
                      <option value="real_estate">Real Estate</option>
                      <option value="insurance">Insurance</option>
                      <option value="banking">Banking</option>
                      <option value="retail">Retail</option>
                      <option value="healthcare">Healthcare</option>
                      <option value="manufacturing">Manufacturing</option>
                      <option value="technology">Technology</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Size *
                    </label>
                    <select
                      required
                      value={formData.size}
                      onChange={(e) => setFormData(prev => ({ ...prev, size: e.target.value as Company['size'] }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    >
                      <option value="small">Small (1-50 employees)</option>
                      <option value="medium">Medium (51-250 employees)</option>
                      <option value="large">Large (251-1000 employees)</option>
                      <option value="enterprise">Enterprise (1000+ employees)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subscription Tier *
                    </label>
                    <select
                      required
                      value={formData.subscription_tier}
                      onChange={(e) => setFormData(prev => ({ ...prev, subscription_tier: e.target.value as Company['subscription_tier'] }))}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    >
                      <option value="basic">Basic</option>
                      <option value="professional">Professional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Updating...' : 'Update Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}